import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchAdminOverview, fetchAdminUsers } from '../store/slices/adminSlice';
import { shouldUseDemoMode } from '../utils/demoMode';
import { DataCacheProvider, useDataCache } from './AdminDataCache';
import {
  X,
  LayoutDashboard,
  Users,
  CreditCard,
  Ticket,
  HelpCircle,
  MessageSquare,
  FileText,
  BookOpen,
  TrendingUp,
  UserCheck,
  DollarSign,
  Activity,
  Send,
  Plus,
  Wallet,
  Search,
  Filter,
  Edit,
  Eye,
  Ban,
  CheckCircle,
  Calendar,
  ArrowUpDown,
  AlertCircle,
  RefreshCw,
  Layout,
  LogOut,
  Crown,
  Bell,
  Mail,
  Clock,
  User,
  Settings,
  Zap,
  Gift,
  AlertTriangle,
  UserPlus,
  Sliders,
  Palette,
  Shield,
  ShieldCheck,
  ListChecks,
  Globe,
  ExternalLink,
  Percent,
  Menu,
  Trash2,
  Sparkles,
  Upload,
  Heart,
  ChevronLeft,
  CheckCheck,
  Phone,
  Smartphone
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { RichTextEditor } from './RichTextEditor';
import { AdminSettings } from './admin/AdminSettings';
import AdminPremiumPlans from './admin/AdminPremiumPlans';
import { ProfileBoosterSettings } from './admin/ProfileBoosterSettings';
import { AdminCommissions } from './admin/AdminCommissions';
import AdminNotifications from './admin/AdminNotifications';
import { DiscoverySettings } from './admin/DiscoverySettings';
import { AdminReportedUsers } from './admin/AdminReportedUsers';
import { AdminRecharge } from './admin/AdminRecharge';
import { DailyInterestModalSettings } from './admin/DailyInterestModalSettings';
import { NewInterestModalSettings } from './admin/NewInterestModalSettings';
import { AdminPlans } from './admin/AdminPlans';
import ReferralAdminSection from './admin/ReferralAdminSection';
import { AdminProfileVerification } from './admin/AdminProfileVerification';
import PendingProfileChanges from './admin/PendingProfileChanges';
import { AdminRoleManagement } from './admin/AdminRoleManagement';
import { AdminAffiliateCommissions } from './admin/AdminAffiliateCommissions';
import { AdminAffiliateManagement } from './admin/AdminAffiliateManagement';
import { PaginationWrapper } from './ui/PaginationWrapper';
import { AdminWordPressSites } from './admin/AdminWordPressSites';
import AdminWordPressAdsEnhanced from './admin/AdminWordPressAdsEnhanced';
import AdminFAQ from './admin/AdminFAQ';
import AdminContactSettings from './admin/AdminContactSettings';
import AdminChatSettings from './admin/AdminChatSettings';
import AdminPushNotificationSettings from './admin/AdminPushNotificationSettings';
import ProfileFieldsConfig from './admin/ProfileFieldsConfig';
import SendEmailTab from './admin/SendEmailTab';
import { useAuthContext } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useTheme, themes } from './ThemeProvider';
import { ThemeSelector } from './ThemeSelector';
import { API_URL, apiClient } from '../lib/api-client';
import { EnvConfigModal } from './EnvConfigModal';
import { EnhancedCouponsView } from './admin/EnhancedCouponsView';
import AdminAssignLeads from './admin/AdminAssignLeads';
import AdminLeadsSettings from './admin/AdminLeadsSettings';
import AdminMarketingManagerLeads from './admin/AdminMarketingManagerLeads';
import AdminEmailNotifications from './admin/AdminEmailNotifications';
import AdminSMTPAccounts from './admin/AdminSMTPAccounts';
import AdminBulkEmailSender from './admin/AdminBulkEmailSender';
import AdminGSTSettings from './admin/AdminGSTSettings';
import DeleteAccountRequests from './admin/DeleteAccountRequests';
import AdminDummyProfiles from './admin/AdminDummyProfiles';
import ProfileImporter from './admin/ProfileImporter';
import { AdminPromotionalOrders } from './admin/AdminPromotionalOrders';
import { AdminPromotionalOrderSettings } from './admin/AdminPromotionalOrderSettings';
import { logger } from '../utils/logger';
import { AdminRewards } from './admin/AdminRewards';
import { AdminUserContacts } from './admin/AdminUserContacts';
import AdminAdSettings from './admin/AdminAdSettings';
import AdminCollaborateRequests from './admin/AdminCollaborateRequests';
import { SmsCampaignSettingsAdmin } from './admin/SmsCampaignSettingsAdmin';
import { SmsDispatchLogsAdmin } from './admin/SmsDispatchLogsAdmin';

interface AdminDashboardProps {
  onBack?: () => void;
  onClose?: () => void;
}

type AdminView = 'overview' | 'app-banner' | 'users' | 'payments' | 'earnings' | 'coupons' | 'help' | 'chats' | 'pages' | 'blogs' | 'footer' | 'premium' | 'booster-settings' | 'commissions' | 'notifications' | 'discovery-settings' | 'reported-users' | 'recharge' | 'plans' | 'referrals' | 'profile-verification' | 'assign-leads' | 'manager-leads' | 'leads-settings' | 'pending-profile-changes' | 'theme-settings' | 'role-management' | 'affiliate-commissions' | 'wordpress-sites' | 'wordpress-ads' | 'affiliate-management' | 'faq' | 'contact-settings' | 'chat-settings' | 'push-notification-settings' | 'profile-fields-config' | 'send-email' | 'email-notifications' | 'smtp-accounts' | 'bulk-email' | 'gst-settings' | 'delete-requests' | 'dummy-profiles' | 'daily-modal-settings' | 'new-interest-modal-settings' | 'profile-importer' | 'promotional-orders' | 'promotional-settings' | 'rewards' | 'user-contacts' | 'ad-settings' | 'collaborate-requests' | 'sms-campaign-settings' | 'sms-dispatch-logs';

export default function AdminDashboardReal({ onClose, onBack }: AdminDashboardProps) {
  const params = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<AdminView>((params.section as AdminView) || 'overview');
  const [loading, setLoading] = useState(false);
  const [isEnvConfigOpen, setIsEnvConfigOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout } = useAuthContext();
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    if (params.section && params.section !== currentView) {
      setCurrentView(params.section as AdminView);
    }
  }, [params.section]);

  const handleViewChange = (viewId: AdminView) => {
    setCurrentView(viewId);
    setSidebarOpen(false);
    navigate(`/admin/${viewId}`, { replace: true });
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const menuItems = [
    { id: 'overview' as AdminView, icon: LayoutDashboard, label: 'Overview' },
    { id: 'app-banner' as AdminView, icon: Smartphone, label: 'App Banner (Play Store)' },
    { id: 'sms-campaign-settings' as AdminView, icon: Send, label: 'SMS Campaign' },
    { id: 'sms-dispatch-logs' as AdminView, icon: ShieldCheck, label: 'SMS Logs' },
    { id: 'users' as AdminView, icon: Users, label: 'User Management' },
    { id: 'role-management' as AdminView, icon: Shield, label: 'Role Management' },
    { id: 'profile-verification' as AdminView, icon: UserCheck, label: 'Profile Verification' },
    { id: 'pending-profile-changes' as AdminView, icon: Edit, label: 'Profile Edit Approvals' },
    { id: 'reported-users' as AdminView, icon: AlertCircle, label: 'Reported Users' },
    { id: 'delete-requests' as AdminView, icon: Trash2, label: 'Delete Requests' },
    { id: 'recharge' as AdminView, icon: Zap, label: 'Recharge User' },
    { id: 'plans' as AdminView, icon: CreditCard, label: 'Subscription Plans' },
    { id: 'payments' as AdminView, icon: DollarSign, label: 'Payments' },
    { id: 'earnings' as AdminView, icon: Wallet, label: 'User Earnings' },
    { id: 'referrals' as AdminView, icon: Gift, label: 'Referral & Wallet' },
    { id: 'rewards' as AdminView, icon: Gift, label: 'User Rewards' },
    { id: 'commissions' as AdminView, icon: DollarSign, label: 'Commissions' },
    { id: 'premium' as AdminView, icon: Crown, label: 'Premium Plans' },
    { id: 'booster-settings' as AdminView, icon: TrendingUp, label: 'Booster Settings' },
    { id: 'discovery-settings' as AdminView, icon: Settings, label: 'Discovery Settings' },
    { id: 'daily-modal-settings' as AdminView, icon: Sparkles, label: 'Daily Interest Modal' },
    { id: 'new-interest-modal-settings' as AdminView, icon: Heart, label: 'New Interest Modal' },
    { id: 'theme-settings' as AdminView, icon: Palette, label: 'Theme Settings' },
    { id: 'coupons' as AdminView, icon: Ticket, label: 'Coupons' },
    { id: 'assign-leads' as AdminView, icon: UserPlus, label: 'Assign Leads' },
    { id: 'manager-leads' as AdminView, icon: ListChecks, label: 'Marketing Manager Leads' },
    { id: 'leads-settings' as AdminView, icon: Sliders, label: 'Leads Settings' },
    { id: 'notifications' as AdminView, icon: Bell, label: 'Push Notifications' },
    { id: 'email-notifications' as AdminView, icon: Mail, label: 'Email Notifications' },
    { id: 'smtp-accounts' as AdminView, icon: Mail, label: 'SMTP Accounts' },
    { id: 'bulk-email' as AdminView, icon: Send, label: 'Bulk Email Sender' },
    { id: 'gst-settings' as AdminView, icon: Percent, label: 'GST Settings' },
    { id: 'help' as AdminView, icon: HelpCircle, label: 'Help & Support' },
    { id: 'chats' as AdminView, icon: MessageSquare, label: 'Chat Monitoring' },
    { id: 'pages' as AdminView, icon: FileText, label: 'Static Pages' },
    { id: 'blogs' as AdminView, icon: BookOpen, label: 'Blog Management' },
    { id: 'footer' as AdminView, icon: Layout, label: 'Footer Settings' },
    { id: 'affiliate-commissions' as AdminView, icon: DollarSign, label: 'Affiliate Commissions' },
    { id: 'affiliate-management' as AdminView, icon: UserCheck, label: 'Affiliate Management' },
    { id: 'wordpress-sites' as AdminView, icon: Globe, label: 'WordPress Sites' },
    { id: 'wordpress-ads' as AdminView, icon: ExternalLink, label: 'WordPress Ads' },
    { id: 'faq' as AdminView, icon: HelpCircle, label: 'FAQ' },
    { id: 'contact-settings' as AdminView, icon: MessageSquare, label: 'Contact Settings' },
    { id: 'chat-settings' as AdminView, icon: MessageSquare, label: 'Chat Settings' },
    { id: 'push-notification-settings' as AdminView, icon: Bell, label: 'Push Notification Settings' },
    { id: 'profile-fields-config' as AdminView, icon: Sliders, label: 'Profile Fields Config' },
    { id: 'send-email' as AdminView, icon: Mail, label: 'Send Email' },
    { id: 'dummy-profiles' as AdminView, icon: Users, label: 'Dummy Profiles' },
    { id: 'profile-importer' as AdminView, icon: Upload, label: 'Profile Importer' },
    { id: 'promotional-orders' as AdminView, icon: Sparkles, label: 'Promotional Orders' },
    { id: 'promotional-settings' as AdminView, icon: Settings, label: 'Promotional Settings' },
    { id: 'user-contacts' as AdminView, icon: Phone, label: 'User Contacts' },
    { id: 'ad-settings' as AdminView, icon: ExternalLink, label: 'Google Ads (AdMob)' },
    { id: 'collaborate-requests' as AdminView, icon: Users, label: 'Collaborate Requests' },
  ];

  return (
    <DataCacheProvider>
      <div className="h-screen flex bg-gray-50">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
        >
          <Menu className="size-6" />
        </button>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-gradient-to-b ${currentTheme.primary} text-white flex flex-col shadow-xl transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
          <div className="p-6 border-b border-white/10">
            <h1 className="text-2xl font-bold">Admin Panel</h1>
            <p className="text-sm text-white/70 mt-1">Atootbandan Management</p>
          </div>
          <nav className="flex-1 p-4 space-y-2 overflow-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleViewChange(item.id)}
                  data-view={item.id}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
                    ? 'bg-white/20 text-white backdrop-blur-sm shadow-md'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                >
                  <Icon className="size-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/30 hover:text-white transition-colors"
            >
              <LogOut className="size-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col md:ml-0">
          {/* Header */}
          <header className="bg-white border-b px-4 md:px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg md:text-2xl font-semibold ml-12 md:ml-0">
              {menuItems.find(item => item.id === currentView)?.label}
            </h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsEnvConfigOpen(true)}
                title="Environment Configuration"
              >
                <Settings className="size-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={onBack || onClose}>
                <X className="size-6" />
              </Button>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-auto p-3 md:p-6">
            {currentView === 'overview' && <OverviewView />}
            {currentView === 'app-banner' && <AdminSettings />}
            {currentView === 'sms-campaign-settings' && <SmsCampaignSettingsAdmin />}
            {currentView === 'sms-dispatch-logs' && <SmsDispatchLogsAdmin />}
            {currentView === 'users' && <UsersView />}
            {currentView === 'role-management' && <AdminRoleManagement />}
            {currentView === 'profile-verification' && <AdminProfileVerification />}
            {currentView === 'pending-profile-changes' && <PendingProfileChanges />}
            {currentView === 'reported-users' && <AdminReportedUsers />}
            {currentView === 'delete-requests' && <DeleteAccountRequests />}
            {currentView === 'recharge' && <AdminRecharge />}
            {currentView === 'plans' && <AdminPlans />}
            {currentView === 'payments' && <PaymentsView />}
            {currentView === 'earnings' && <EarningsView />}
            {currentView === 'referrals' && <ReferralAdminSection />}
            {currentView === 'rewards' && <AdminRewards />}
            {currentView === 'coupons' && <EnhancedCouponsView currentTheme={currentTheme} />}
            {currentView === 'assign-leads' && <AdminAssignLeads />}
            {currentView === 'manager-leads' && <AdminMarketingManagerLeads />}
            {currentView === 'leads-settings' && <AdminLeadsSettings />}
            {currentView === 'commissions' && <AdminCommissions />}
            {currentView === 'premium' && <AdminPremiumPlans />}
            {currentView === 'booster-settings' && <ProfileBoosterSettings />}
            {currentView === 'discovery-settings' && <DiscoverySettings />}
            {currentView === 'daily-modal-settings' && <DailyInterestModalSettings />}
            {currentView === 'new-interest-modal-settings' && <NewInterestModalSettings />}
            {currentView === 'theme-settings' && <ThemeSettingsView />}
            {currentView === 'notifications' && <AdminNotifications />}
            {currentView === 'email-notifications' && <AdminEmailNotifications />}
            {currentView === 'smtp-accounts' && <AdminSMTPAccounts />}
            {currentView === 'bulk-email' && <AdminBulkEmailSender />}
            {currentView === 'gst-settings' && <AdminGSTSettings />}
            {currentView === 'help' && <HelpView />}
            {currentView === 'chats' && <ChatsView />}
            {currentView === 'pages' && <PagesView />}
            {currentView === 'blogs' && <BlogsView />}
            {currentView === 'footer' && <AdminSettings />}
            {currentView === 'affiliate-commissions' && <AdminAffiliateCommissions />}
            {currentView === 'affiliate-management' && <AdminAffiliateManagement />}
            {currentView === 'wordpress-sites' && <AdminWordPressSites />}
            {currentView === 'wordpress-ads' && <AdminWordPressAdsEnhanced />}
            {currentView === 'faq' && <AdminFAQ />}
            {currentView === 'contact-settings' && <AdminContactSettings />}
            {currentView === 'chat-settings' && <AdminChatSettings />}
            {currentView === 'push-notification-settings' && <AdminPushNotificationSettings />}
            {currentView === 'profile-fields-config' && <ProfileFieldsConfig />}
            {currentView === 'send-email' && <SendEmailTab />}
            {currentView === 'dummy-profiles' && <AdminDummyProfiles />}
            {currentView === 'profile-importer' && <ProfileImporter />}
            {currentView === 'promotional-orders' && <AdminPromotionalOrders />}
            {currentView === 'promotional-settings' && <AdminPromotionalOrderSettings />}
            {currentView === 'user-contacts' && <AdminUserContacts />}
            {currentView === 'ad-settings' && <AdminAdSettings />}
            {currentView === 'collaborate-requests' && <AdminCollaborateRequests />}
          </main>
        </div>

        {/* Environment Configuration Modal */}
        <EnvConfigModal
          isOpen={isEnvConfigOpen}
          onClose={() => setIsEnvConfigOpen(false)}
        />
      </div>
    </DataCacheProvider>
  );
}

// Theme Settings View
function ThemeSettingsView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className={`p-3 rounded-lg bg-gradient-to-r ${currentTheme.primary}`}>
            <Palette className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Theme Settings</h2>
            <p className="text-gray-600 text-sm mt-1">
              Change the global theme color for all users across the platform
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Admin-Only Setting</p>
              <p>Only administrators can change the theme. The selected theme will be applied globally to all users on the platform.</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <ThemeSelector standalone={true} onClose={() => { }} />
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-2">Current Active Theme</h3>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${currentTheme.primary} shadow-md`}></div>
            <div>
              <p className="font-semibold text-gray-900">{currentTheme.name}</p>
              <p className="text-sm text-gray-600">Applied globally to all users</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Overview View with Real Data
function OverviewView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const dispatch = useAppDispatch();
  const { overview } = useAppSelector(state => state.admin);
  const { stats, recentActivities, loading, refreshing } = overview as any;

  useEffect(() => {
    dispatch(fetchAdminOverview(false));
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchAdminOverview(true));
  };

  if (loading && !stats) {
    return (
      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-6 bg-white rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 bg-gray-200 animate-pulse rounded-lg" />
                <div className="h-4 w-16 bg-gray-100 animate-pulse rounded" />
              </div>
              <div className="h-8 bg-gray-200 animate-pulse w-24 mb-2 rounded" />
              <div className="h-4 bg-gray-100 animate-pulse w-32 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers?.toLocaleString() || '0', icon: Users, color: 'bg-blue-500' },
    { label: 'Active Users', value: stats?.activeUsers?.toLocaleString() || '0', icon: UserCheck, color: 'bg-green-500' },
    { label: 'Revenue', value: `₹${((stats?.totalRevenue || 0) / 100000).toFixed(2)}L`, icon: DollarSign, color: 'bg-purple-500' },
    { label: 'Success Rate', value: `${stats?.successRate || 0}%`, icon: TrendingUp, color: currentTheme.badge },
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <span className="text-sm md:text-base text-gray-600 font-medium">{stat.label}</span>
                <div className={`${stat.color} p-2 md:p-3 rounded-lg`}>
                  <Icon className="size-4 md:size-6 text-white" />
                </div>
              </div>
              <div className="text-2xl md:text-3xl font-bold">{stat.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Recent Activities</h3>
          <div className="space-y-3">
            {recentActivities?.length > 0 ? (
              recentActivities.map((activity: any, index: number) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Activity className="size-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium">{activity.type || 'Activity'}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(activity.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No recent activities</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Quick Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active Subscriptions</span>
              <span className="text-xl font-bold">{stats?.activeSubscriptions || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Revenue</span>
              <span className="text-xl font-bold">₹{((stats?.totalRevenue || 0) / 100).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Users View with Real MongoDB Data
function UsersView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const dispatch = useAppDispatch();
  const { users: usersState } = useAppSelector(state => state.admin);
  const { list: users, loading, pagination } = usersState;
  const { total: totalUsers, totalPages } = pagination;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    dispatch(fetchAdminUsers({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      plan: planFilter !== 'all' ? planFilter : undefined,
      role: roleFilter !== 'all' ? roleFilter : undefined
    }));
  }, [dispatch, currentPage, itemsPerPage, searchTerm, statusFilter, planFilter, roleFilter]);

  const fetchUsers = (forceRefresh = false) => {
    dispatch(fetchAdminUsers({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      plan: planFilter !== 'all' ? planFilter : undefined,
      role: roleFilter !== 'all' ? roleFilter : undefined
    }));
  };

  const handleToggleUser = async (userId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/admin/users/${userId}/status`, {
        isActive: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`User ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchUsers(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };


  const handleActivateAll = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const inactiveUsers = users.filter(u => !u.isActive);
      if (inactiveUsers.length === 0) {
        toast.info('All users are already active');
        return;
      }

      await Promise.all(
        inactiveUsers.map(user =>
          axios.patch(`${API_URL}/admin/users/${user._id}`, { isActive: true })
        )
      );

      toast.success(`Activated ${inactiveUsers.length} users successfully`);
      fetchUsers();
    } catch (error: any) {
      logger.error('Error activating users:', error);
      toast.error(error.response?.data?.message || 'Failed to activate users');
    }
  };

  // Check for duplicates in current page
  const emailCounts = new Map();
  const phoneCounts = new Map();
  users.forEach(user => {
    if (user.email) {
      emailCounts.set(user.email, (emailCounts.get(user.email) || 0) + 1);
    }
    if (user.phone) {
      phoneCounts.set(user.phone, (phoneCounts.get(user.phone) || 0) + 1);
    }
  });

  // Backend handles filtering and pagination - just display the returned users
  const paginatedUsers = users;
  const filteredUsers = users; // Alias for compatibility

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, planFilter, roleFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className={`size-8 animate-spin ${currentTheme.text}`} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg md:text-xl font-semibold">User Filters</h3>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="flex-1 sm:flex-none">
              <Filter className="size-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchUsers} className="flex-1 sm:flex-none">
              <RefreshCw className="size-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <Label>Search</Label>
              <Input
                placeholder="Name, email, user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 md:p-6 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-lg md:text-xl font-semibold">All Users</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredUsers.length} users found</p>
          </div>
          <Button
            onClick={handleActivateAll}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <CheckCircle className="size-4" />
            <span className="hidden md:inline">Activate All Inactive Users</span>
            <span className="md:hidden">Activate All</span>
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">User ID</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Name</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Email</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Referred By</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Plan</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Role</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Status</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    <Users className="size-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No users found</p>
                    <p className="text-sm">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : paginatedUsers.map((user) => {
                const hasDuplicateEmail = user.email && emailCounts.get(user.email) > 1;
                const hasDuplicatePhone = user.phone && phoneCounts.get(user.phone) > 1;
                return (
                  <tr key={user._id} className={`hover:bg-gray-50 ${(hasDuplicateEmail || hasDuplicatePhone) ? 'bg-red-50/50' : ''}`}>
                    <td className="p-2 md:p-4">
                      <div className="text-xs font-mono text-gray-600 truncate max-w-[80px]">{user._id}</div>
                    </td>
                    <td className="p-2 md:p-4">
                      <div>
                        <div className="font-medium text-xs md:text-sm">{user.firstName} {user.lastName}</div>
                        <div className="text-xs text-gray-500">
                          {user.city && user.city !== 'N/A' && user.state && user.state !== 'N/A'
                            ? `${user.city}, ${user.state}`
                            : user.gender && user.gender !== 'N/A'
                              ? `${user.gender}${user.age ? `, ${user.age} yrs` : ''}`
                              : user.age
                                ? `${user.age} yrs`
                                : 'Profile incomplete'
                          }
                        </div>
                      </div>
                    </td>
                    <td className="p-2 md:p-4">
                      <div className="text-sm">
                        {user.email}
                        {hasDuplicateEmail && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertTriangle className="size-3 text-red-600/80" />
                            <span className="text-xs text-red-600/80">Duplicate Email</span>
                          </div>
                        )}
                        {hasDuplicatePhone && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertTriangle className="size-3 text-red-600/80" />
                            <span className="text-xs text-red-600/80">Duplicate Phone</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-2 md:p-4">
                      {user.referredBy ? (
                        <div className="text-xs">
                          <span className="font-mono font-medium text-green-700">{user.referredBy}</span>
                          <div className="text-xs text-green-600 mt-0.5">✓ Referred</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-2 md:p-4">
                      <Badge className={`text-xs ${user.currentPlan?.toLowerCase().includes('platinum') ? 'bg-purple-100 text-purple-700' :
                        user.currentPlan?.toLowerCase().includes('gold') || user.currentPlan?.toLowerCase().includes('premium') ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                        {user.currentPlan || 'Free'}
                      </Badge>
                    </td>
                    <td className="p-2 md:p-4">
                      <Badge className={`text-xs ${user.role === 'admin' ? `${currentTheme.badge} opacity-80` : user.role === 'marketing' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {user.role || 'user'}
                      </Badge>
                    </td>
                    <td className="p-2 md:p-4">
                      <Badge className={`text-xs ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-2 md:p-4">
                      <div className="flex items-center gap-1 md:gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedUser(user)} className="h-8 w-8 p-0">
                          <Eye className="size-3 md:size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleToggleUser(user._id, user.isActive)}
                          title={user.isActive ? 'Disable User' : 'Enable User'}
                        >
                          {user.isActive ? (
                            <Ban className={`size-3 md:size-4 text-red-600`} />
                          ) : (
                            <CheckCircle className="size-3 md:size-4 text-green-600" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalUsers > 0 && (
          <div className="p-4 md:p-6 border-t">
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalUsers}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              showItemsPerPage={true}
              itemsPerPageOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>

      {/* User Detail Dialog */}
      {selectedUser && (
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="max-w-[95vw] md:max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base md:text-lg">User Details - {selectedUser.name}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-sm text-gray-500">Full Name</Label>
                <p className="font-medium">{selectedUser.name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Email</Label>
                <p className="font-medium">{selectedUser.email}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Phone</Label>
                <p className="font-medium">{selectedUser.phone || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Age & Gender</Label>
                <p className="font-medium">{selectedUser.age || 'N/A'} yrs, {selectedUser.gender || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Religion</Label>
                <p className="font-medium">{selectedUser.religion || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Education</Label>
                <p className="font-medium">{selectedUser.education || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Occupation</Label>
                <p className="font-medium">{selectedUser.occupation || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">City</Label>
                <p className="font-medium">{selectedUser.city || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Plan</Label>
                <Badge className="mt-1">{selectedUser.plan || 'Free'}</Badge>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Role</Label>
                <Badge className="mt-1">{selectedUser.role || 'user'}</Badge>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Refer Code</Label>
                <p className="font-mono font-medium">{selectedUser.referCode || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Referred By</Label>
                <p className="font-mono font-medium">{selectedUser.referredBy || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Joined Date</Label>
                <p className="font-medium">
                  {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button
                variant={selectedUser.isActive ? 'destructive' : 'default'}
                onClick={() => {
                  handleToggleUser(selectedUser._id, selectedUser.isActive);
                  setSelectedUser(null);
                }}
              >
                {selectedUser.isActive ? 'Disable User' : 'Enable User'}
              </Button>
              <Button variant="outline" onClick={() => setSelectedUser(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Payments View with Real MongoDB Data
function PaymentsView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { cache, updateCache, shouldRefresh } = useDataCache();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPayments, setTotalPayments] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchPayments();
  }, [currentPage, itemsPerPage, searchTerm, statusFilter]);

  const fetchPayments = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/admin/payments`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined
        }
      });

      const responseData = response.data.data || response.data;
      const paymentsData = responseData.payments || responseData || [];
      const pagination = responseData.pagination || {};

      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setTotalPayments(pagination.total || paymentsData.length);
      setTotalPages(pagination.totalPages || Math.ceil((pagination.total || paymentsData.length) / itemsPerPage));

      if (forceRefresh) {
        toast.success('Payments data refreshed');
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Backend handles filtering and pagination
  const paginatedPayments = payments;
  const filteredPayments = payments; // Alias for compatibility

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className={`size-8 animate-spin ${currentTheme.text}`} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg md:text-xl font-semibold">Payment Filters</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchPayments(true)}
            disabled={refreshing}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`size-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Search</Label>
            <Input
              placeholder="Order ID, Payment ID or Email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 md:p-6 border-b">
          <h3 className="text-lg md:text-xl font-semibold">Payment Transactions</h3>
          <p className="text-sm text-gray-500 mt-1">{filteredPayments.length} transactions found</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Invoice</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">User</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Plan</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Payment Details</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Amount</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Date</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Status</th>
                <th className="text-left p-2 md:p-4 font-semibold text-xs md:text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedPayments.map((payment) => (
                <tr key={payment._id} className="hover:bg-gray-50">
                  <td className="p-2 md:p-4">
                    <div className="text-sm">
                      <div className="font-mono text-xs text-gray-600">
                        {payment.invoice?.invoiceNumber || 'N/A'}
                      </div>
                      <div className="font-mono text-xs text-gray-400 mt-1">
                        ID: {payment._id?.slice(-8)}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 md:p-4">
                    <div className="text-sm">
                      <div className="font-medium text-xs md:text-sm">{payment.userId?.firstName} {payment.userId?.lastName}</div>
                      <div className="text-gray-500 text-xs truncate max-w-[150px]">{payment.userId?.email || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="p-2 md:p-4">
                    <div className="text-sm">
                      <div className="font-medium">{payment.planId?.name || 'N/A'}</div>
                      <div className="text-xs text-gray-500">
                        {payment.planType === 'user_plan' ? 'User Plan' : 'Premium Plan'}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-xs space-y-1">
                      {payment.paymentId && (
                        <div className="font-mono bg-gray-50 px-2 py-1 rounded">
                          <span className="text-gray-500">Pay: </span>
                          {payment.paymentId}
                        </div>
                      )}
                      {payment.razorpayOrderId && (
                        <div className="font-mono bg-gray-50 px-2 py-1 rounded">
                          <span className="text-gray-500">Order: </span>
                          {payment.razorpayOrderId}
                        </div>
                      )}
                      {payment.refundId && (
                        <div className="font-mono bg-red-50/50 px-2 py-1 rounded text-red-700/80">
                          <span className="text-red-500/80">Refund: </span>
                          {payment.refundId}
                        </div>
                      )}
                      <div className="text-gray-500 capitalize">
                        {payment.paymentMethod || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">
                      <div className="font-semibold">₹{(payment.finalAmount || 0).toLocaleString()}</div>
                      {payment.discountAmount > 0 && (
                        <div className="text-xs text-green-600">
                          -₹{payment.discountAmount.toLocaleString()} discount
                        </div>
                      )}
                      {payment.refundAmount > 0 && (
                        <div className="text-xs text-red-600/80">
                          -₹{payment.refundAmount.toLocaleString()} refunded
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-sm">
                    <div className="text-xs">
                      {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      }) : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {payment.createdAt ? new Date(payment.createdAt).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : ''}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <Badge className={
                        payment.paymentStatus === 'success' || payment.paymentStatus === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : payment.paymentStatus === 'failed'
                            ? 'bg-gray-100 text-gray-700'
                            : payment.paymentStatus === 'refunded'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-yellow-100 text-yellow-700'
                      }>
                        {payment.paymentStatus || 'pending'}
                      </Badge>
                      {payment.subscription?.isActive && (
                        <div className="text-xs text-green-600">Active</div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const details = `
PAYMENT DETAILS
================
Invoice: ${payment.invoice?.invoiceNumber || 'N/A'}
Order ID: ${payment._id}

USER INFO
=========
Name: ${payment.userId?.firstName} ${payment.userId?.lastName}
Email: ${payment.userId?.email}
Phone: ${payment.userId?.phone || 'N/A'}

PLAN DETAILS
============
Plan: ${payment.planId?.name}
Type: ${payment.planType === 'user_plan' ? 'User Plan' : 'Premium Plan'}
Duration: ${payment.planId?.duration || 'N/A'} months

PAYMENT INFO
============
Original Amount: ₹${payment.originalAmount?.toLocaleString()}
Discount: ₹${payment.discountAmount?.toLocaleString()}
Final Amount: ₹${payment.finalAmount?.toLocaleString()}
Payment Method: ${payment.paymentMethod}
Currency: ${payment.currency}

RAZORPAY DETAILS
================
Payment ID: ${payment.paymentId || 'N/A'}
Order ID: ${payment.razorpayOrderId || 'N/A'}
Signature: ${payment.razorpaySignature ? 'Verified ✓' : 'N/A'}

STATUS
======
Payment Status: ${payment.paymentStatus}
Overall Status: ${payment.status}
Subscription Active: ${payment.subscription?.isActive ? 'Yes' : 'No'}
${payment.subscription?.startDate ? `Start: ${new Date(payment.subscription.startDate).toLocaleDateString()}` : ''}
${payment.subscription?.endDate ? `End: ${new Date(payment.subscription.endDate).toLocaleDateString()}` : ''}

${payment.refundId ? `
REFUND DETAILS
==============
Refund ID: ${payment.refundId}
Refund Amount: ₹${payment.refundAmount?.toLocaleString()}
Refund Status: ${payment.refund?.status || 'N/A'}
Refunded At: ${payment.refund?.refundedAt ? new Date(payment.refund.refundedAt).toLocaleString() : 'N/A'}
Reason: ${payment.refund?.reason || 'N/A'}
` : ''}

${payment.commission?.marketingUserId ? `
COMMISSION
==========
Marketing User: ${payment.commission.marketingUserId.email || 'N/A'}
Commission: ₹${payment.commission.amount?.toLocaleString()}
Status: ${payment.commission.status}
` : ''}

TIMELINE
========
Created: ${new Date(payment.createdAt).toLocaleString()}
Updated: ${new Date(payment.updatedAt).toLocaleString()}
${payment.statusHistory?.map(h => `${h.timestamp ? new Date(h.timestamp).toLocaleString() : ''} - ${h.status}: ${h.note || ''}`).join('\n')}
                        `.trim();
                        alert(details);
                      }}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredPayments.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No payments found
          </div>
        )}
        {totalPayments > 0 && (
          <div className="p-4 border-t">
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalPayments}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// User Earnings View with Real MongoDB Data
function EarningsView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { cache, updateCache, shouldRefresh, invalidateCache } = useDataCache();
  const [earnings, setEarnings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchEarnings();
  }, [currentPage, itemsPerPage, statusFilter]);

  const fetchEarnings = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/admin/earnings`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          status: statusFilter !== 'all' ? statusFilter : undefined
        }
      });

      const responseData = response.data.data || response.data;
      const earningsData = responseData.earnings || responseData || [];
      const pagination = responseData.pagination || {};

      setEarnings(Array.isArray(earningsData) ? earningsData : []);
      setTotalEarnings(pagination.total || earningsData.length);
      setTotalPages(pagination.totalPages || Math.ceil((pagination.total || earningsData.length) / itemsPerPage));

      if (forceRefresh) {
        toast.success('Earnings data refreshed');
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleApproveEarning = async (earningId: string) => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await axios.patch(`${API_URL}/admin/earnings/${earningId}/approve`);
      invalidateCache('earnings');
      fetchEarnings();
    } catch (error) {
    }
  };

  const handleRejectEarning = async (earningId: string) => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await axios.patch(`${API_URL}/admin/earnings/${earningId}/reject`);
      invalidateCache('earnings');
      fetchEarnings();
    } catch (error) {
    }
  };

  // Backend handles filtering and pagination
  const paginatedEarnings = earnings;
  const filteredEarnings = earnings; // Alias for compatibility

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className={`size-8 animate-spin ${currentTheme.text}`} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Earnings Filters</h3>
          <Button
            variant="outline"
            onClick={() => fetchEarnings(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`size-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Earnings Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b">
          <h3 className="text-xl font-semibold">User Earnings</h3>
          <p className="text-sm text-gray-500 mt-1">{totalEarnings} earnings found</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold">Marketing User</th>
                <th className="text-left p-4 font-semibold">Amount</th>
                <th className="text-left p-4 font-semibold">From Payment</th>
                <th className="text-left p-4 font-semibold">Date</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedEarnings.map((earning) => (
                <tr key={earning._id} className="hover:bg-gray-50">
                  <td className="p-4 font-medium">{earning.userId?.email || 'N/A'}</td>
                  <td className="p-4 font-semibold">₹{(earning.amount || 0).toLocaleString()}</td>
                  <td className="p-4 text-sm">{earning.paymentId?.orderId || 'N/A'}</td>
                  <td className="p-4 text-sm">
                    {earning.createdAt ? new Date(earning.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-4">
                    <Badge className={
                      earning.status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : earning.status === 'rejected'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-yellow-100 text-yellow-700'
                    }>
                      {earning.status || 'pending'}
                    </Badge>
                  </td>
                  <td className="p-4">
                    {earning.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleApproveEarning(earning._id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRejectEarning(earning._id)}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalEarnings > 0 && (
          <div className="p-4 border-t">
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalEarnings}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Coupons View with Real MongoDB Data (REPLACED WITH EnhancedCouponsView)
function CouponsView_OLD() {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount: 10,
    selectedPlan: '',
    expiryDays: 30,
    maxUsageLimit: 0,
    description: ''
  });

  // 🔥 Add theme hook to access currentTheme
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch coupons and plans using the correct API
      const [couponsRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/coupons`),
        axios.get(`${API_URL}/plans`)
      ]);

      if (couponsRes.data.success) {
        setCoupons(couponsRes.data.data.coupons || []);
      }

      if (plansRes.data.success) {
        setPlans(plansRes.data.data.plans || []);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoupon = async () => {
    if (!formData.code || !formData.selectedPlan) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.code.length < 4) {
      toast.error('Coupon code must be at least 4 characters');
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Calculate expiry date
      const validUntilDate = new Date();
      validUntilDate.setDate(validUntilDate.getDate() + formData.expiryDays);

      const response = await axios.post(`${API_URL}/coupons`, {
        code: formData.code.toUpperCase(),
        planId: formData.selectedPlan,
        discountPercentage: formData.discount,
        maxUses: formData.maxUsageLimit,
        validUntil: validUntilDate.toISOString(),
        description: formData.description || ''
      });

      if (response.data.success) {
        toast.success('Coupon created successfully!');
        setIsOpen(false);
        setFormData({
          code: '',
          discount: 10,
          selectedPlan: '',
          expiryDays: 30,
          maxUsageLimit: 0,
          description: ''
        });
        fetchData();
      }
    } catch (error: any) {
      logger.error('Error creating coupon:', error);
      toast.error(error.response?.data?.message || 'Failed to create coupon');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCoupon = async (couponId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.put(`${API_URL}/coupons/${couponId}/toggle`);

      if (response.data.success) {
        toast.success(`Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchData();
      }
    } catch (error) {
      logger.error('Error toggling coupon:', error);
      toast.error('Failed to update coupon status');
    }
  };

  const formatCurrency = (amount: number) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '₹0';
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="size-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Coupon Management</h3>
            <p className="text-sm text-gray-500 mt-1">{coupons.length} coupons found</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="size-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button className={`${currentTheme.gradient} hover:opacity-90`}>
                  <Plus className="size-4 mr-2" />
                  Add Coupon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Coupon</DialogTitle>
                </DialogHeader>

                {/* Form - SAME AS MARKETING PANEL */}
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border-2 border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <Label>Coupon Code *</Label>
                      <Input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="e.g., SAVE20"
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">Min 4 characters</p>
                    </div>

                    <div>
                      <Label>Discount Percentage *</Label>
                      <Select
                        value={formData.discount.toString()}
                        onValueChange={(value) => setFormData({ ...formData, discount: Number(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="10">10%</SelectItem>
                          <SelectItem value="15">15%</SelectItem>
                          <SelectItem value="20">20%</SelectItem>
                          <SelectItem value="25">25%</SelectItem>
                          <SelectItem value="30">30%</SelectItem>
                          <SelectItem value="40">40%</SelectItem>
                          <SelectItem value="50">50%</SelectItem>
                          <SelectItem value="60">60%</SelectItem>
                          <SelectItem value="70">70%</SelectItem>
                          <SelectItem value="80">80%</SelectItem>
                          <SelectItem value="90">90%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Applicable Plan *</Label>
                      <Select
                        value={formData.selectedPlan}
                        onValueChange={(value) => setFormData({ ...formData, selectedPlan: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map(plan => (
                            <SelectItem key={plan._id} value={plan._id}>
                              {plan.name} - {formatCurrency(plan.finalPrice || plan.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Valid For (Days)</Label>
                      <Input
                        type="number"
                        value={formData.expiryDays}
                        onChange={(e) => setFormData({ ...formData, expiryDays: Number(e.target.value) })}
                        min="1"
                        max="365"
                      />
                      <p className="text-xs text-gray-500 mt-1">Days until expiration</p>
                    </div>

                    <div>
                      <Label>Usage Limit (0 for unlimited)</Label>
                      <Input
                        type="number"
                        value={formData.maxUsageLimit}
                        onChange={(e) => setFormData({ ...formData, maxUsageLimit: Number(e.target.value) })}
                        min="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max times this can be used</p>
                    </div>

                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="e.g., Limited time offer"
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">For internal reference</p>
                    </div>
                  </div>

                  <div className="flex gap-2 sm:gap-3">
                    <Button
                      onClick={handleAddCoupon}
                      disabled={creating || !formData.code || !formData.selectedPlan}
                      className={`flex-1 ${currentTheme.gradient} hover:opacity-90`}
                    >
                      {creating ? (
                        <>
                          <RefreshCw className="size-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Coupon'
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold">Code</th>
                <th className="text-left p-4 font-semibold">Discount</th>
                <th className="text-left p-4 font-semibold">Description</th>
                <th className="text-left p-4 font-semibold">Valid Until</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {coupons.map((coupon) => (
                <tr key={coupon._id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <span className="font-mono font-bold bg-blue-50 text-blue-700 px-3 py-1 rounded">
                      {coupon.code}
                    </span>
                  </td>
                  <td className="p-4 font-semibold">
                    {coupon.discountPercentage || 0}%
                  </td>
                  <td className="p-4 text-sm">{coupon.description || '-'}</td>
                  <td className="p-4 text-sm">
                    {coupon.validUntil ? new Date(coupon.validUntil).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-4">
                    <Badge className={coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {coupon.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Switch
                      checked={coupon.isActive}
                      onCheckedChange={() => handleToggleCoupon(coupon._id, coupon.isActive)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Help & Support View with Real MongoDB Data
function HelpView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { cache, updateCache, shouldRefresh } = useDataCache();
  const [queries, setQueries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (cache.help && !shouldRefresh('help')) {
      setQueries(cache.help);
      setLoading(false);
    } else {
      fetchHelpQueries();
    }
  }, []);

  const fetchHelpQueries = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/admin/help-queries`);
      const data = response.data.data || [];
      setQueries(data);
      updateCache('help', data);

      if (forceRefresh) {
        toast.success('Help queries refreshed');
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className={`size-8 animate-spin ${currentTheme.text}`} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">Help & Support Queries</h3>
        <Button variant="outline" onClick={fetchHelpQueries}>
          <RefreshCw className="size-4 mr-2" />
          Refresh
        </Button>
      </div>
      <div className="space-y-4">
        {queries.length > 0 ? (
          queries.map((query: any) => (
            <div key={query._id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium">{query.subject || 'Support Query'}</p>
                  <p className="text-sm text-gray-600 mt-1">{query.message}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    From: {query.userId?.email || 'Anonymous'} |
                    {query.createdAt ? new Date(query.createdAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <Badge className={query.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                  {query.status || 'pending'}
                </Badge>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center py-8">No support queries found</p>
        )}
      </div>
    </div>
  );
}

// Chat Monitoring View with Real MongoDB Data
function ChatsView() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { cache, updateCache, shouldRefresh } = useDataCache();
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAsUserId, setReplyAsUserId] = useState<string>('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Helper function to get participant display name
  const getParticipantName = (p: any) => {
    if (!p) return 'Unknown User';
    if (typeof p === 'object' && p.firstName) {
      return `${p.firstName} ${p.lastName || ''}`.trim();
    }
    return typeof p === 'string' ? `User: ...${p.slice(-6)}` : 'Unknown User';
  };

  // Helper function to get participant email or ID
  const getParticipantEmail = (p: any) => {
    if (!p) return 'N/A';
    if (typeof p === 'object' && p.email) return p.email;
    return typeof p === 'string' ? `ID: ...${p.slice(-8)}` : 'N/A';
  };

  // Helper function to get participant ID
  const getParticipantId = (p: any) => {
    if (!p) return Math.random().toString();
    if (typeof p === 'object' && p._id) return p._id;
    return typeof p === 'string' ? p : Math.random().toString();
  };

  useEffect(() => {
    if (cache.chats && !shouldRefresh('chats')) {
      // Ensure cache.chats is an array
      const cachedChats = Array.isArray(cache.chats) ? cache.chats : [];
      setConversations(cachedChats);
      setLoading(false);
    } else {
      fetchConversations();
    }
  }, []);

  const fetchConversations = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Check if demo mode or if backend is not accessible
      const isDemoMode = shouldUseDemoMode();

      if (isDemoMode) {
        logger.log('🎭 Admin Chat Monitor: Using demo data');
        // Use demo data for chat monitoring
        const demoChats = [
          {
            _id: 'demo-chat-1',
            participants: [
              {
                _id: 'demo-user-1',
                firstName: 'John',
                lastName: 'Doe',
                email: 'john@example.com',
                profile: { gender: 'male', age: 28 }
              },
              {
                _id: 'demo-user-2',
                firstName: 'Jane',
                lastName: 'Smith',
                email: 'jane@example.com',
                profile: { gender: 'female', age: 26 }
              }
            ],
            lastMessage: {
              content: 'Hi! Nice to meet you.',
              senderId: 'demo-user-1',
              timestamp: new Date().toISOString()
            },
            unreadCount: 2,
            isActive: true,
            isBlocked: false,
            status: 'active',
            messages: [
              {
                _id: 'msg-1',
                senderId: 'demo-user-1',
                content: 'Hi! Nice to meet you.',
                messageType: 'text',
                isRead: false,
                createdAt: new Date().toISOString()
              }
            ],
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            _id: 'demo-chat-2',
            participants: [
              {
                _id: 'demo-user-3',
                firstName: 'Michael',
                lastName: 'Brown',
                email: 'michael@example.com',
                profile: { gender: 'male', age: 30 }
              },
              {
                _id: 'demo-user-4',
                firstName: 'Sarah',
                lastName: 'Wilson',
                email: 'sarah@example.com',
                profile: { gender: 'female', age: 27 }
              }
            ],
            lastMessage: {
              content: 'Would love to connect!',
              senderId: 'demo-user-4',
              timestamp: new Date(Date.now() - 3600000).toISOString()
            },
            unreadCount: 0,
            isActive: true,
            isBlocked: false,
            status: 'active',
            messages: [
              {
                _id: 'msg-2',
                senderId: 'demo-user-3',
                content: 'Hello!',
                messageType: 'text',
                isRead: true,
                createdAt: new Date(Date.now() - 7200000).toISOString()
              },
              {
                _id: 'msg-3',
                senderId: 'demo-user-4',
                content: 'Would love to connect!',
                messageType: 'text',
                isRead: true,
                createdAt: new Date(Date.now() - 3600000).toISOString()
              }
            ],
            createdAt: new Date(Date.now() - 172800000).toISOString(),
            updatedAt: new Date(Date.now() - 3600000).toISOString()
          }
        ];

        setConversations(demoChats);
        toast.info('📊 Viewing demo chat data. Connect to backend for real data.');
        return;
      }

      const token = localStorage.getItem('token');

      if (!token) {
        // Silent fail
        // logger.error('No authentication token found');
        toast.error('Authentication required. Please login again.');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      logger.log('🔍 Admin Chat Monitor: Fetching conversations from:', `${API_URL}/admin/chats`);

      // Try admin-specific endpoint first, then fallback to user endpoint
      let response;
      try {
        response = await axios.get(`${API_URL}/admin/chats`, {
          timeout: 10000 // 10 second timeout
        });
      } catch (adminError: any) {
        logger.log('⚠️ Admin endpoint failed, trying user endpoint:', adminError.message);
        // Fallback to user endpoint if admin endpoint fails
        response = await axios.get(`${API_URL}/chats`, {
          timeout: 10000
        });
      }

      logger.log('🔍 Admin Chat Monitor: API Response:', response.data);

      // The API returns { success: true, data: { conversations: [...], pagination: {...} } }
      let chats = response.data.data?.conversations || response.data.conversations || response.data.data?.chats || response.data.chats || [];

      // Ensure chats is always an array
      if (!Array.isArray(chats)) {
        logger.log('⚠️ Admin Chat Monitor: Response is not an array, converting to empty array. Received:', typeof chats, chats);
        chats = [];
      }

      logger.log('✅ Admin Chat Monitor: Fetched', chats.length, 'chats');

      // Log the first chat to see the structure
      if (chats.length > 0) {
        logger.log('🔍 Admin Chat Monitor: First chat structure:', chats[0]);
        logger.log('🔍 Admin Chat Monitor: Participants in first chat:', chats[0].participants);
      } else {
        logger.log('⚠️ Admin Chat Monitor: No chats found in response');
        logger.log('🔍 Admin Chat Monitor: Full response data:', response.data);
      }

      setConversations(chats);
      updateCache('chats', chats);

      if (forceRefresh) {
        toast.success('Conversations refreshed');
      }
    } catch (error: any) {
      // Silent fail in production
      // logger.error('Error fetching conversations:', error);

      // Better error messages with fallback to demo data
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Silent fail - network error
        // logger.error('Network error details:', {
        //   apiUrl: API_URL,
        //   message: 'Backend server may not be running or accessible',
        //   suggestion: 'Make sure the backend server is running on the correct port'
        // });

        // Show demo data instead of error
        toast.info('Backend not available. Showing demo chat data.');
        const demoChats = [
          {
            _id: 'demo-chat-1',
            participants: [
              {
                _id: 'demo-user-1',
                firstName: 'John',
                lastName: 'Doe',
                email: 'john@example.com',
                profile: { gender: 'male', age: 28 }
              },
              {
                _id: 'demo-user-2',
                firstName: 'Jane',
                lastName: 'Smith',
                email: 'jane@example.com',
                profile: { gender: 'female', age: 26 }
              }
            ],
            lastMessage: {
              content: 'Hi! Nice to meet you.',
              senderId: 'demo-user-1',
              timestamp: new Date().toISOString()
            },
            unreadCount: 2,
            isActive: true,
            isBlocked: false,
            status: 'active',
            messages: [
              {
                _id: 'msg-1',
                senderId: 'demo-user-1',
                content: 'Hi! Nice to meet you.',
                messageType: 'text',
                isRead: false,
                createdAt: new Date().toISOString()
              }
            ],
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            updatedAt: new Date().toISOString()
          }
        ];
        setConversations(demoChats);
      } else if (error.response) {
        toast.error(`Server error: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        toast.error('No response from server. Please check your connection.');
      } else {
        toast.error('Failed to fetch conversations');
      }

      // Ensure conversations is always an array even on error
      setConversations([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    try {
      // Check if demo mode or if backend is not accessible
      const isDemoMode = shouldUseDemoMode();

      if (isDemoMode || conversationId.startsWith('demo-')) {
        // Use messages from the selected conversation's demo data
        const conversation = conversations.find(c => c._id === conversationId);
        if (conversation && conversation.messages) {
          setMessages(conversation.messages);
        }
        return;
      }

      const token = localStorage.getItem('token');

      if (!token) {
        // Silent fail
        // logger.error('No authentication token found');
        toast.error('Authentication required. Please login again.');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      logger.log('🔍 Admin Chat Monitor: Fetching messages for conversation:', conversationId);

      // Try admin endpoint first, fallback to user endpoint
      let response;
      try {
        response = await axios.get(`${API_URL}/admin/chats/${conversationId}/messages`, {
          timeout: 10000 // 10 second timeout
        });
      } catch (adminError: any) {
        logger.log('⚠️ Admin messages endpoint failed, trying user endpoint');
        response = await axios.get(`${API_URL}/chats/${conversationId}/messages`, {
          timeout: 10000
        });
      }

      logger.log('🔍 Admin Chat Monitor: Messages response:', response.data);

      // The API returns { success: true, data: { chat: { messages: [...] } } }
      const chat = response.data.data?.chat || {};
      const messagesList = chat.messages || response.data.data?.messages || [];
      logger.log('✅ Admin Chat Monitor: Fetched', messagesList.length, 'messages');
      setMessages(messagesList);
    } catch (error: any) {
      // Silent fail in production
      // logger.error('Error fetching messages:', error);

      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Fallback to conversation's messages if available
        const conversation = conversations.find(c => c._id === conversationId);
        if (conversation && conversation.messages) {
          setMessages(conversation.messages);
          toast.info('Showing cached messages (backend not available)');
        } else {
          toast.error('Unable to connect to server.');
        }
      } else if (error.response) {
        toast.error(`Failed to fetch messages: ${error.response.data?.message || error.response.statusText}`);
      } else {
        toast.error('Failed to fetch messages');
      }
    }
  };

  const handleViewConversation = (conversation: any) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation._id);
    // Set default reply user to first participant
    if (conversation.participants?.[0]?._id) {
      setReplyAsUserId(conversation.participants[0]._id);
    }
  };

  const handleSendAdminMessage = async () => {
    if (!replyMessage.trim() || !replyAsUserId || !selectedConversation) {
      toast.error('Please enter a message and select a user');
      return;
    }

    try {
      setSendingMessage(true);

      // Check if demo mode
      const isDemoMode = shouldUseDemoMode();

      if (isDemoMode) {
        // In demo mode, just add the message locally
        const sender = selectedConversation.participants?.find((p: any) => p._id === replyAsUserId);
        const newMessage = {
          _id: `demo-msg-${Date.now()}`,
          content: replyMessage,
          senderId: sender || replyAsUserId,
          conversationId: selectedConversation._id,
          createdAt: new Date().toISOString(),
          isRead: false,
          messageType: 'text'
        };

        setMessages([...messages, newMessage]);
        setReplyMessage('');
        toast.success('✅ Message sent successfully (demo mode)');
      } else {
        // Real API call
        const response = await apiClient.post(
          `/admin/chats/${selectedConversation._id}/reply-as-user`,
          {
            content: replyMessage,
            sendAsUserId: replyAsUserId,
          }
        );

        if (response.data.success) {
          // Refresh messages to show the new one
          await fetchMessages(selectedConversation._id);
          setReplyMessage('');
          toast.success('Message sent successfully');
        }
      }
    } catch (error: any) {
      // Silent fail
      // logger.error('Error sending message:', error);
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Unable to send message.');
      } else {
        toast.error(`Failed to send message: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const filteredConversations = Array.isArray(conversations) ? conversations.filter(conv => {
    if (!searchQuery.trim()) return true;
    const searchLower = searchQuery.toLowerCase();
    return conv.participants?.some((p: any) =>
      p.email?.toLowerCase().includes(searchLower) ||
      p.firstName?.toLowerCase().includes(searchLower) ||
      p.lastName?.toLowerCase().includes(searchLower)
    );
  }) : [];

  // Pagination logic
  const totalPages = Math.ceil(filteredConversations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedConversations = filteredConversations.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Ensure currentPage doesn't exceed totalPages when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className={`size-8 animate-spin ${currentTheme.text}`} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards - Responsive Grid */}
      {conversations.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Total Conversations</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">{conversations.length}</p>
              </div>
              <div className="bg-blue-200/50 p-3 rounded-lg">
                <MessageSquare className="size-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Active Today</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {conversations.filter(conv => {
                    const today = new Date().toDateString();
                    return new Date(conv.updatedAt).toDateString() === today;
                  }).length}
                </p>
              </div>
              <div className="bg-green-200/50 p-3 rounded-lg">
                <Activity className="size-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Unique Users</p>
                <p className="text-2xl font-bold text-purple-900 mt-1">
                  {new Set(conversations.flatMap(c => c.participants?.map((p: any) => p._id) || [])).size}
                </p>
              </div>
              <div className="bg-purple-200/50 p-3 rounded-lg">
                <Users className="size-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State when no conversations */}
      {conversations.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="bg-gray-100 rounded-full p-6 w-24 h-24 mx-auto mb-4 flex items-center justify-center">
            <MessageSquare className="size-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Conversations Yet</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            There are no chat conversations in the system yet. Conversations will appear here once users start messaging each other.
          </p>
          <Button onClick={() => fetchConversations(true)} variant="outline" className="gap-2">
            <RefreshCw className="size-4" />
            Refresh
          </Button>
        </div>
      )}

      {/* Split View: Chat List + Messages - Responsive */}
      {conversations.length > 0 && (
        <div className="h-[calc(100vh-280px)] sm:h-[calc(100vh-240px)] flex flex-col lg:flex-row gap-4">
          {/* Chat List - Left Sidebar - Hidden on mobile when chat selected */}
          <div className={`${selectedConversation ? 'hidden lg:flex' : 'flex'} lg:w-1/3 xl:w-2/5 bg-white rounded-xl shadow-sm flex-col overflow-hidden`}>
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="size-5 text-rose-500" />
                  Conversations
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchConversations(true)}
                  disabled={refreshing}
                  className="gap-2"
                >
                  <RefreshCw className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Chat List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {paginatedConversations.length > 0 ? (
                paginatedConversations.map((conv: any) => {
                  const recentMessages = conv.messages?.slice(-10) || [];
                  const lastMessage = recentMessages[recentMessages.length - 1];

                  return (
                    <div
                      key={conv._id}
                      onClick={() => handleViewConversation(conv)}
                      className={`border rounded-xl p-3 cursor-pointer transition-all hover:shadow-md group ${selectedConversation?._id === conv._id
                        ? 'bg-gradient-to-r from-rose-50 to-pink-50 border-rose-300 shadow-sm'
                        : 'hover:bg-gray-50 hover:border-gray-300'
                        }`}
                    >
                      {/* Header with names and badge */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                            <Users className="size-4 text-gray-500 flex-shrink-0" />
                            <span className="truncate">
                              {conv.participants?.map((p: any) => `${p.firstName} ${p.lastName}`).join(' & ')}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                            <Clock className="size-3" />
                            {conv.updatedAt ? new Date(conv.updatedAt).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </div>
                        </div>
                        {conv.unreadCount > 0 && (
                          <div className="flex-shrink-0 ml-2">
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-rose-500 text-white text-xs font-semibold rounded-full">
                              {conv.unreadCount}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Last Message Preview */}
                      {lastMessage && (
                        <div className="bg-white/60 rounded-lg p-2 mb-2">
                          <div className="text-xs">
                            <span className="font-medium text-gray-700">
                              {conv.participants?.find((p: any) => p._id === (typeof lastMessage.senderId === 'object' ? lastMessage.senderId?._id : lastMessage.senderId))?.firstName || 'Unknown'}:
                            </span>
                            <span className="text-gray-600 ml-1">
                              {lastMessage.content?.substring(0, 40)}{lastMessage.content?.length > 40 ? '...' : ''}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Message Count */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 flex items-center gap-1">
                          <MessageSquare className="size-3" />
                          {recentMessages.length} message{recentMessages.length !== 1 ? 's' : ''}
                        </span>
                        {conv.isActive ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <div className="size-2 bg-green-500 rounded-full animate-pulse"></div>
                            Active
                          </span>
                        ) : (
                          <span className="text-gray-400">Inactive</span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <Search className="size-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    {searchQuery ? 'No conversations match your search' : 'No conversations found'}
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredConversations.length > 0 && (
              <div className="p-3 border-t bg-gray-50">
                <PaginationWrapper
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filteredConversations.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                  showItemsPerPage={true}
                  itemsPerPageOptions={[10, 20, 50, 100]}
                />
              </div>
            )}
          </div>

          {/* Chat Monitor - Right Main Area - Full width on mobile when selected */}
          <div className={`${!selectedConversation ? 'hidden lg:flex' : 'flex'} flex-1 bg-white rounded-xl shadow-sm flex-col overflow-hidden`}>
            {selectedConversation ? (
              <>
                {/* Header with Back Button for Mobile */}
                <div className="p-4 border-b bg-gradient-to-r from-rose-50 via-pink-50 to-white">
                  <div className="flex items-center gap-3 mb-3">
                    {/* Back button - visible on mobile */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedConversation(null)}
                      className="lg:hidden"
                    >
                      <ChevronLeft className="size-5" />
                    </Button>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">
                        {selectedConversation.participants?.map((p: any) => `${p.firstName} ${p.lastName}`).join(' & ')}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="size-3" />
                          <span className="hidden sm:inline">{selectedConversation.participants?.map((p: any) => p.email).join(', ')}</span>
                          <span className="sm:hidden">{selectedConversation.participants?.[0]?.email}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <UserCheck className="size-3" />
                          {selectedConversation.participants?.map((p: any) =>
                            `${p.profile?.gender || 'N/A'}, ${p.profile?.age || 'N/A'} yrs`
                          ).join(' | ')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Messages - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-50 to-white space-y-4">
                  {messages.length > 0 ? (
                    messages.map((msg: any) => {
                      const senderId = typeof msg.senderId === 'object' ? msg.senderId?._id : msg.senderId;
                      const sender = typeof msg.senderId === 'object'
                        ? msg.senderId
                        : selectedConversation.participants?.find((p: any) => p._id === senderId);
                      const isFirstUser = selectedConversation.participants?.[0]?._id === senderId;

                      return (
                        <div key={msg._id} className={`flex flex-col ${isFirstUser ? 'items-start' : 'items-end'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`size-6 rounded-full flex items-center justify-center text-xs font-semibold ${isFirstUser ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white'
                              }`}>
                              {sender?.firstName?.[0] || '?'}
                            </div>
                            <span className="text-xs text-gray-600 font-medium">
                              {sender?.firstName} {sender?.lastName}
                            </span>
                            <span className="text-xs text-gray-400">
                              {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </span>
                          </div>
                          <div className={`relative p-3 rounded-2xl max-w-[85%] sm:max-w-md shadow-sm ${isFirstUser
                            ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-tl-sm'
                            : 'bg-white border border-gray-200 text-gray-900 rounded-tr-sm'
                            }`}>
                            <p className="text-sm break-words">{msg.content}</p>
                            {msg.messageType && msg.messageType !== 'text' && (
                              <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-lg ${isFirstUser ? 'bg-blue-700/50' : 'bg-gray-100'
                                }`}>
                                📎 {msg.messageType}
                              </span>
                            )}
                          </div>
                          {msg.isRead && (
                            <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                              <CheckCheck className="size-3" />
                              Read
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <MessageSquare className="size-16 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No messages in this conversation</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Admin Reply Section - Sticky Bottom */}
                <div className="border-t bg-white shadow-lg">
                  {/* Stats Bar */}
                  <div className="px-4 py-2 border-b bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <MessageSquare className="size-3" />
                        {messages.length} messages
                      </span>
                      <span className="flex items-center gap-1">
                        {selectedConversation.isActive ? (
                          <>
                            <div className="size-2 bg-green-500 rounded-full animate-pulse"></div>
                            Active
                          </>
                        ) : (
                          <>
                            <div className="size-2 bg-red-500 rounded-full"></div>
                            Inactive
                          </>
                        )}
                      </span>
                      <span className="hidden sm:inline">
                        Created: {new Date(selectedConversation.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Reply As User Selection */}
                  <div className="p-4">
                    <div className="mb-3">
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <UserCheck className="size-4 text-rose-500" />
                        Send Message As:
                      </Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedConversation.participants?.map((participant: any, index: number) => (
                          <Button
                            key={participant._id || index}
                            type="button"
                            variant={replyAsUserId === participant._id ? "default" : "outline"}
                            size="sm"
                            onClick={() => setReplyAsUserId(participant._id)}
                            className={`justify-start ${replyAsUserId === participant._id
                              ? 'bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 shadow-md'
                              : ''
                              }`}
                          >
                            <User className="size-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{participant.firstName} {participant.lastName}</span>
                            <span className="ml-auto text-xs opacity-75">
                              {index === 0 ? 'User 1' : 'User 2'}
                            </span>
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Message Input */}
                    <div>
                      <Label className="text-sm mb-2 block">Type Message:</Label>
                      <div className="flex gap-2">
                        <Input
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder={`Type as ${selectedConversation.participants?.find((p: any) => p._id === replyAsUserId)?.firstName || 'select user'}...`}
                          onKeyDown={(e) => e.key === 'Enter' && !sendingMessage && handleSendAdminMessage()}
                          disabled={sendingMessage}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleSendAdminMessage}
                          disabled={sendingMessage || !replyMessage.trim() || !replyAsUserId}
                          className="bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 shadow-md"
                        >
                          {sendingMessage ? (
                            <RefreshCw className="size-4 animate-spin" />
                          ) : (
                            <Send className="size-4" />
                          )}
                        </Button>
                      </div>
                      {!replyAsUserId && (
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="size-3" />
                          Please select a user to send the message as
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 p-8">
                <div className="text-center max-w-sm">
                  <div className="bg-gray-100 rounded-full p-8 w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                    <MessageSquare className="size-16 text-gray-300" />
                  </div>
                  <p className="text-lg font-medium text-gray-600 mb-2">Select a conversation</p>
                  <p className="text-sm text-gray-500">Choose a chat from the left sidebar to view and monitor messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Static Pages Management
function PagesView() {
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // 🔥 Add theme hook to access currentTheme
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/admin/pages`);
      setPages(response.data.data || []);
    } catch (error) {
      // Silently handle errors
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async () => {
    try {
      if (!formData.title || !formData.content) {
        return;
      }

      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      if (editingPage) {
        // Update existing page
        await axios.patch(`${API_URL}/admin/pages/${editingPage._id}`, formData);
      } else {
        // Create new page
        await axios.post(`${API_URL}/admin/pages`, formData);
      }

      setIsOpen(false);
      setEditingPage(null);
      setFormData({
        title: '',
        slug: '',
        content: ''
      });
      fetchPages();
    } catch (error) {
      // Silently handle errors
    }
  };

  const handleEditPage = (page: any) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content
    });
    setIsOpen(true);
  };

  const handleDeletePage = async (pageId: string) => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await axios.delete(`${API_URL}/admin/pages/${pageId}`);
      // Remove from state instead of fetching all pages again
      setPages(prevPages => prevPages.filter(page => page._id !== pageId));
    } catch (error) {
      // Silently handle errors
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="size-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold">Static Pages Management</h3>
            <p className="text-sm text-gray-500 mt-1">Manage pages like About, Terms, Privacy Policy, etc. ({pages.length} pages)</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchPages}>
              <RefreshCw className="size-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={isOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) {
                setEditingPage(null);
                setFormData({ title: '', slug: '', content: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className={`${currentTheme.gradient} hover:opacity-90`}>
                  <Plus className="size-4 mr-2" />
                  Create New Page
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPage ? 'Edit Page' : 'Create New Page'}</DialogTitle>
                  <DialogDescription>
                    {editingPage ? 'Update the page details' : 'Create a new static page accessible at /pages/[slug]'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      placeholder="About Us"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Slug (URL-friendly, auto-generated if empty) *</Label>
                    <Input
                      placeholder="about-us"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Page will be accessible at: /pages/{formData.slug || 'your-slug'}
                    </p>
                  </div>
                  <div>
                    <Label>Content * (HTML supported)</Label>
                    <RichTextEditor
                      value={formData.content}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                    />
                  </div>
                  <Button
                    onClick={handleCreatePage}
                    className="w-full"
                    disabled={!formData.title || !formData.content}
                  >
                    {editingPage ? 'Update Page' : 'Create Page'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {pages.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold">Title</th>
                  <th className="text-left p-4 font-semibold">Slug</th>
                  <th className="text-left p-4 font-semibold">Created</th>
                  <th className="text-left p-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pages.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((page) => (
                  <tr key={page._id} className="hover:bg-gray-50">
                    <td className="p-4 font-medium">{page.title}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">/pages/{page.slug}</code>
                        <a
                          href={`/pages/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-rose-500 hover:text-rose-600 text-sm"
                          title="Open page in new tab"
                        >
                          <ExternalLink className="size-4" />
                        </a>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        https://atootbandan.com/pages/{page.slug}
                      </div>
                    </td>
                    <td className="p-4 text-sm">
                      {page.createdAt ? new Date(page.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditPage(page)}
                        >
                          <Edit className="size-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive">
                              <X className="size-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Page</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{page.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeletePage(page._id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <FileText className="size-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No pages created yet</p>
            <p className="text-sm mt-1">Create your first page to get started</p>
          </div>
        )}

        {/* Pagination */}
        {pages.length > 0 && (
          <div className="p-4 border-t">
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={Math.ceil(pages.length / itemsPerPage)}
              onPageChange={setCurrentPage}
              totalItems={pages.length}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Blog Management View with Real MongoDB Data
function BlogsView() {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    tags: '',
    featuredImage: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Comments management states
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedBlogForComments, setSelectedBlogForComments] = useState<any>(null);
  const [adminNewComment, setAdminNewComment] = useState('');
  const [replyInputs, setReplyInputs] = useState<Record<string, string>>({});

  // 🔥 Add theme hook to access currentTheme
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    if (shouldUseDemoMode()) {
      setBlogs([
        {
          _id: 'demo-blog-001',
          title: "10 Tips for Creating the Perfect Matrimonial Profile",
          excerpt: "Learn how to make your profile stand out and attract the right matches with these expert tips.",
          content: "<p>Creating an engaging profile is crucial for success in online matrimony...</p><p>Make sure to write a detailed bio and add a high-quality picture.</p>",
          slug: "perfect-matrimonial-profile",
          authorType: 'guest',
          guestAuthor: {
            name: 'Dr. Priya Sharma',
            email: 'priya@example.com',
            bio: 'Relationship consultant with 10+ years experience.'
          },
          category: "relationship_advice",
          views: 1250,
          createdAt: new Date().toISOString(),
          status: 'published',
          comments: [
            {
              _id: 'c1',
              name: 'Amit Patel',
              email: 'amit@example.com',
              content: 'Very helpful tips! Thanks for sharing.',
              isApproved: true,
              createdAt: new Date().toISOString()
            },
            {
              _id: 'c2',
              name: 'Neha Gupta',
              email: 'neha@example.com',
              content: 'Can you write more about choosing the profile picture?',
              isApproved: false,
              createdAt: new Date().toISOString()
            }
          ]
        },
        {
          _id: 'demo-blog-002',
          title: "Understanding Compatibility: Beyond the Basics",
          excerpt: "Discover the key factors that contribute to long-term relationship success and compatibility.",
          content: "<p>Compatibility is more than just shared interests...</p><p>Communication, values, and lifecycle goals are much more critical.</p>",
          slug: "understanding-compatibility",
          authorType: 'admin',
          author: {
            firstName: 'System',
            lastName: 'Admin',
            email: 'admin@test.com'
          },
          category: "relationship_advice",
          views: 980,
          createdAt: new Date().toISOString(),
          status: 'published',
          comments: []
        }
      ]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/admin/blogs`);
      setBlogs(response.data.data || []);
    } catch (error) {
      // logger.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      tags: '',
      featuredImage: ''
    });
    setIsOpen(true);
  };

  const handleOpenEdit = (blog: any) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content,
      excerpt: blog.excerpt || '',
      tags: blog.tags?.join(', ') || '',
      featuredImage: blog.featuredImage?.url || blog.featuredImage || ''
    });
    setIsOpen(true);
  };

  const handleSaveBlog = async () => {
    try {
      if (!formData.title || !formData.content) {
        toast.error('Title and content are required');
        return;
      }

      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);

      const payload = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt || formData.title,
        tags: tagsArray,
        featuredImage: formData.featuredImage ? { url: formData.featuredImage } : undefined,
        category: 'relationship_advice'
      };

      if (shouldUseDemoMode()) {
        if (editingBlog) {
          setBlogs(prev => prev.map(b => b._id === editingBlog._id ? { ...b, ...payload } : b));
          toast.success('Blog updated successfully (Demo Mode)');
        } else {
          const newBlog = {
            _id: `demo-${Date.now()}`,
            ...payload,
            authorType: 'admin',
            author: { firstName: 'System', lastName: 'Admin', email: 'admin@test.com' },
            views: 0,
            status: 'published',
            createdAt: new Date().toISOString(),
            comments: []
          };
          setBlogs(prev => [newBlog, ...prev]);
          toast.success('Blog created successfully (Demo Mode)');
        }
        setIsOpen(false);
        setEditingBlog(null);
        return;
      }

      if (editingBlog) {
        await axios.put(`${API_URL.replace('/admin', '')}/blogs/${editingBlog._id}`, payload);
        toast.success('Blog updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/blogs`, payload);
        toast.success('Blog created successfully');
      }

      setIsOpen(false);
      setEditingBlog(null);
      fetchBlogs();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save blog');
    }
  };

  const handleDeleteBlog = async (blogId: string) => {
    if (shouldUseDemoMode()) {
      setBlogs(prev => prev.filter(b => b._id !== blogId));
      toast.success('Blog deleted successfully (Demo Mode)');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await axios.delete(`${API_URL.replace('/admin', '')}/blogs/${blogId}`);
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error: any) {
      toast.error('Failed to delete blog');
    }
  };

  const handleUpdateBlogStatus = async (blogId: string, status: string) => {
    if (shouldUseDemoMode()) {
      setBlogs(prev => prev.map(b => b._id === blogId ? { ...b, status } : b));
      toast.success(`Blog status updated to ${status} (Demo Mode)`);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await axios.patch(`${API_URL}/admin/blogs/${blogId}`, { status });
      fetchBlogs();
      toast.success(`Blog status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update blog status');
    }
  };

  const handleToggleCommentApproval = async (commentId: string) => {
    if (!selectedBlogForComments) return;
    const updatedComments = selectedBlogForComments.comments.map((c: any) => {
      if (c._id === commentId) {
        return { ...c, isApproved: !c.isApproved };
      }
      return c;
    });

    if (shouldUseDemoMode()) {
      setSelectedBlogForComments({ ...selectedBlogForComments, comments: updatedComments });
      setBlogs(prev => prev.map(b => b._id === selectedBlogForComments._id ? { ...b, comments: updatedComments } : b));
      toast.success('Comment status updated (Demo Mode)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.put(`${API_URL.replace('/admin', '')}/blogs/${selectedBlogForComments._id}`, {
        comments: updatedComments
      });

      if (response.data.success) {
        setSelectedBlogForComments(response.data.data.blog || { ...selectedBlogForComments, comments: updatedComments });
        toast.success('Comment status updated');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Failed to update comment status');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedBlogForComments || !window.confirm('Delete this comment?')) return;
    const updatedComments = selectedBlogForComments.comments.filter((c: any) => c._id !== commentId);

    if (shouldUseDemoMode()) {
      setSelectedBlogForComments({ ...selectedBlogForComments, comments: updatedComments });
      setBlogs(prev => prev.map(b => b._id === selectedBlogForComments._id ? { ...b, comments: updatedComments } : b));
      toast.success('Comment deleted (Demo Mode)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.put(`${API_URL.replace('/admin', '')}/blogs/${selectedBlogForComments._id}`, {
        comments: updatedComments
      });

      if (response.data.success) {
        setSelectedBlogForComments(response.data.data.blog || { ...selectedBlogForComments, comments: updatedComments });
        toast.success('Comment deleted');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleAddAdminComment = async () => {
    if (!selectedBlogForComments || !adminNewComment.trim()) return;

    if (shouldUseDemoMode()) {
      const newComment = {
        _id: `c-${Date.now()}`,
        name: 'System Admin',
        email: 'admin@test.com',
        content: adminNewComment,
        isApproved: true,
        createdAt: new Date().toISOString()
      };
      const updatedComments = [...(selectedBlogForComments.comments || []), newComment];
      setSelectedBlogForComments({ ...selectedBlogForComments, comments: updatedComments });
      setBlogs(prev => prev.map(b => b._id === selectedBlogForComments._id ? { ...b, comments: updatedComments } : b));
      setAdminNewComment('');
      toast.success('Comment added successfully (Demo Mode)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL.replace('/admin', '')}/blogs/${selectedBlogForComments._id}/comments`, {
        content: adminNewComment
      });

      if (response.data.success) {
        const blogResp = await axios.get(`${API_URL.replace('/admin', '')}/blogs/${selectedBlogForComments.slug}`);
        if (blogResp.data.success) {
          setSelectedBlogForComments(blogResp.data.data.blog);
        }
        setAdminNewComment('');
        toast.success('Comment added successfully');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleReplyToComment = async (commentId: string, commenterName: string) => {
    const replyText = replyInputs[commentId];
    if (!replyText || !replyText.trim() || !selectedBlogForComments) return;

    if (shouldUseDemoMode()) {
      const replyContent = `Reply to ${commenterName}: ${replyText}`;
      const newComment = {
        _id: `c-${Date.now()}`,
        name: 'System Admin',
        email: 'admin@test.com',
        content: replyContent,
        isApproved: true,
        createdAt: new Date().toISOString()
      };
      const updatedComments = [...(selectedBlogForComments.comments || []), newComment];
      setSelectedBlogForComments({ ...selectedBlogForComments, comments: updatedComments });
      setBlogs(prev => prev.map(b => b._id === selectedBlogForComments._id ? { ...b, comments: updatedComments } : b));
      setReplyInputs(prev => ({ ...prev, [commentId]: '' }));
      toast.success('Reply added successfully (Demo Mode)');
      return;
    }

    try {
      const replyContent = `Reply to ${commenterName}: ${replyText}`;
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL.replace('/admin', '')}/blogs/${selectedBlogForComments._id}/comments`, {
        content: replyContent
      });

      if (response.data.success) {
        const blogResp = await axios.get(`${API_URL.replace('/admin', '')}/blogs/${selectedBlogForComments.slug}`);
        if (blogResp.data.success) {
          setSelectedBlogForComments(blogResp.data.data.blog);
        }
        setReplyInputs(prev => ({ ...prev, [commentId]: '' }));
        toast.success('Reply added successfully');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="size-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold">Blog Management</h3>
            <p className="text-sm text-gray-500 mt-1">{blogs.length} blogs found</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchBlogs}>
              <RefreshCw className="size-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={isOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) {
                setEditingBlog(null);
                setFormData({ title: '', content: '', excerpt: '', tags: '', featuredImage: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className={`${currentTheme.gradient} hover:opacity-90`} onClick={handleOpenCreate}>
                  <Plus className="size-4 mr-2" />
                  Create New Blog
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
                  <DialogDescription>
                    {editingBlog ? 'Edit the details of the blog post' : 'Create a new blog post. The slug will be auto-generated from the title.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      placeholder="Enter blog title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Content * (HTML supported)</Label>
                    <RichTextEditor
                      value={formData.content}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                    />
                  </div>
                  <div>
                    <Label>Excerpt</Label>
                    <Textarea
                      placeholder="Brief description (optional, auto-generated if empty)"
                      value={formData.excerpt}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Tags (comma separated)</Label>
                    <Input
                      placeholder="matrimony, relationships, tips"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Featured Image URL</Label>
                    <Input
                      placeholder="https://example.com/image.jpg"
                      value={formData.featuredImage}
                      onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                    />
                  </div>
                  <Button
                    onClick={handleSaveBlog}
                    className="w-full"
                    disabled={!formData.title || !formData.content}
                  >
                    {editingBlog ? 'Update Blog Post' : 'Create Blog Post'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold">Title</th>
                <th className="text-left p-4 font-semibold">Author</th>
                <th className="text-left p-4 font-semibold">Views</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Date</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {blogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((blog) => (
                <tr key={blog._id} className="hover:bg-gray-50">
                  <td className="p-4 font-medium">{blog.title}</td>
                  <td className="p-4 text-sm">
                    {blog.authorType === 'guest' 
                      ? `${blog.guestAuthor?.name || 'Guest'} (Guest)` 
                      : (blog.author?.firstName 
                          ? `${blog.author.firstName} ${blog.author.lastName}` 
                          : blog.authorId?.email || blog.author?.email || 'Admin')}
                  </td>
                  <td className="p-4 text-sm font-semibold text-gray-700">
                    <span className="flex items-center gap-1">
                      <Eye className="size-4 text-gray-400" />
                      {blog.views || 0}
                    </span>
                  </td>
                  <td className="p-4">
                    <Badge className={
                       blog.status === 'published'
                         ? 'bg-green-100 text-green-700'
                         : blog.status === 'rejected'
                           ? 'bg-red-100 text-red-700'
                           : 'bg-yellow-100 text-yellow-700'
                    }>
                      {blog.status || 'pending'}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm">
                    {blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {blog.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                            onClick={() => handleUpdateBlogStatus(blog._id, 'published')}
                          >
                            Publish
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            onClick={() => handleUpdateBlogStatus(blog._id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        title="Manage Comments"
                        onClick={() => {
                          setSelectedBlogForComments(blog);
                          setCommentsDialogOpen(true);
                        }}
                        className="h-8"
                      >
                        <MessageSquare className="size-4 text-rose-500" />
                        <span className="ml-1 text-xs">({blog.comments?.length || 0})</span>
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        title="Edit Article"
                        onClick={() => handleOpenEdit(blog)}
                        className="h-8"
                      >
                        <Edit className="size-4" />
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="destructive"
                            title="Delete Article"
                            className="h-8"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this blog post? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteBlog(blog._id)} className="bg-red-600 hover:bg-red-700 text-white">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {blogs.length > 0 && (
          <div className="p-4 border-t">
            <PaginationWrapper
              currentPage={currentPage}
              totalPages={Math.ceil(blogs.length / itemsPerPage)}
              onPageChange={setCurrentPage}
              totalItems={blogs.length}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        )}
      </div>

      {/* Comments Dialog */}
      <Dialog open={commentsDialogOpen} onOpenChange={setCommentsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="size-5 text-rose-500" />
              Comments & Replies for "{selectedBlogForComments?.title}"
            </DialogTitle>
            <DialogDescription>
              Moderate existing reader comments, write replies, or add new editorial comments.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 my-4">
            {/* Add New Comment */}
            <div className="bg-gray-50 p-4 rounded-xl border space-y-3">
              <h4 className="font-semibold text-gray-800 text-sm">Add Comment as Admin</h4>
              <div className="flex gap-2">
                <Input
                  value={adminNewComment}
                  onChange={(e) => setAdminNewComment(e.target.value)}
                  placeholder="Type a comment or announcement..."
                  className="flex-1 bg-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAdminComment()}
                />
                <Button onClick={handleAddAdminComment} className={`${currentTheme.gradient}`}>
                  <Send className="size-4" />
                </Button>
              </div>
            </div>

            {/* Comments List */}
            <div className="space-y-4">
              <h4 className="font-bold text-gray-900 text-base">
                Reader Comments ({selectedBlogForComments?.comments?.length || 0})
              </h4>
              {selectedBlogForComments?.comments && selectedBlogForComments.comments.length > 0 ? (
                <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                  {selectedBlogForComments.comments.map((comment: any) => (
                    <div 
                      key={comment._id} 
                      className={`p-4 rounded-xl border transition-all ${
                        comment.isApproved ? 'bg-white border-gray-200' : 'bg-amber-50/50 border-amber-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                            {comment.name}
                            <span className="text-xs text-gray-400 font-normal">({comment.email})</span>
                            {!comment.isApproved && (
                              <Badge className="bg-amber-100 text-amber-800 text-[10px] border-0 h-5 px-2">
                                Pending
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant={comment.isApproved ? "outline" : "default"}
                            className={`h-7 text-xs ${!comment.isApproved ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                            onClick={() => handleToggleCommentApproval(comment._id)}
                          >
                            {comment.isApproved ? 'Unapprove' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            onClick={() => handleDeleteComment(comment._id)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-700 mt-2.5 leading-relaxed bg-gray-50/50 p-2.5 rounded-lg border border-gray-100">
                        {comment.content}
                      </p>

                      {/* Reply Input Box */}
                      <div className="mt-3 flex gap-2 pl-4 border-l-2 border-gray-200">
                        <Input
                          value={replyInputs[comment._id] || ''}
                          onChange={(e) => setReplyInputs(prev => ({ ...prev, [comment._id]: e.target.value }))}
                          placeholder={`Reply to ${comment.name}...`}
                          className="h-8 text-xs bg-white"
                          onKeyDown={(e) => e.key === 'Enter' && handleReplyToComment(comment._id, comment.name)}
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleReplyToComment(comment._id, comment.name)}
                          className={`h-8 ${currentTheme.gradient} text-white text-xs`}
                        >
                          Reply
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl text-gray-400">
                  <MessageSquare className="size-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-medium">No comments on this post yet.</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}